import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { facilitatorAPI, campAPI, uploadAPI, authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { TrustBadge } from '../components/TrustBadge';
import ProfileCompletionIndicator from '../components/ProfileCompletionIndicator';
import VerificationDocuments from '../components/VerificationDocuments';
import VerifiedBadgeShare from '../components/VerifiedBadgeShare';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { LogoMark } from '../components/Logo';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  User, 
  Settings, 
  Save,
  AlertCircle,
  CheckCircle2,
  Upload,
  FileText,
  Image as ImageIcon,
  Mail,
  MailCheck,
  RefreshCw,
  Shield,
  Droplets,
  Eye
} from 'lucide-react';
import { provinces, activities, campTypes, ageGroups, campRoles, facilitatorSpecialties, firstAidLevels } from '../lib/utils';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [resendingEmail, setResendingEmail] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(true);

  useEffect(() => {
    setOnboardingDismissed(user ? !!user.onboarding_dismissed : true);
  }, [user]);

  const dismissOnboarding = async () => {
    setOnboardingDismissed(true);
    try {
      await authAPI.dismissOnboarding();
      if (refreshUser) refreshUser();
    } catch (err) {
      /* keep dismissed locally even if the call fails */
    }
  };

  const goToProfileSection = (section) => {
    const map = {
      basic: '[data-testid="profile-tab-content"]',
      experience: '[data-testid="profile-tab-content"]',
      documents: '[data-testid="verification-documents-section"]',
    };
    const el = document.querySelector(map[section] || '[data-testid="profile-tab-content"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submitForReview = async () => {
    setMessage({ type: 'success', text: 'Your profile has been submitted for review. Our team will verify your documents shortly.' });
    await dismissOnboarding();
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      loadProfile();
    }
  }, [user, authLoading]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      if (user.role === 'facilitator') {
        const response = await facilitatorAPI.getMyProfile();
        setProfile(response.data);
      } else if (user.role === 'camp') {
        const response = await campAPI.getMyProfile();
        setProfile(response.data);
      }
    } catch (err) {
      // Profile doesn't exist yet
      setProfile(null);
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      await authAPI.resendVerification();
      setMessage({ type: 'success', text: 'Verification email sent! Check your inbox.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to send verification email' });
    }
    setResendingEmail(false);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" data-testid="dashboard-loading">
        <LogoMark size="xl" className="animate-pulse" />
        <div className="text-muted-foreground text-sm">Loading your dashboard…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Email Verification Banner */}
        {!user.email_verified && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6" data-testid="email-verification-banner">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Email not verified</p>
                <p className="text-sm text-amber-600">Please verify your email to ensure account security.</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleResendVerification}
              disabled={resendingEmail}
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              data-testid="resend-verification-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${resendingEmail ? 'animate-spin' : ''}`} />
              {resendingEmail ? 'Sending...' : 'Resend Email'}
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
              Dashboard
            </h1>
            <p className="text-muted-foreground">Manage your profile and settings</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Preview Button - only for facilitator/camp users with a profile */}
            {profile && profile.id && (user.role === 'facilitator' || user.role === 'camp') && (
              <Button
                variant="outline"
                onClick={() => {
                  const baseUrl = window.location.origin;
                  const previewPath = user.role === 'facilitator' 
                    ? `/facilitators/${profile.id}?preview=true`
                    : `/camps/${profile.id}?preview=true`;
                  window.open(baseUrl + previewPath, '_blank', 'noopener,noreferrer');
                }}
                className="rounded-full"
                data-testid="preview-profile-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview My Profile
              </Button>
            )}
            {profile && (
              <TrustBadge status={profile.verification_status} />
            )}
          </div>
        </div>

        {/* Profile Completion Indicator - hidden while the onboarding checklist is showing (avoids two overlapping checklists) */}
        {profile && (user.role === 'facilitator' || user.role === 'camp') && !(user.role === 'facilitator' && !onboardingDismissed) && (
          <ProfileCompletionIndicator 
            profile={profile} 
            userRole={user.role}
            onEditField={(fieldKey) => {
              // Scroll to profile tab content
              const profileContent = document.querySelector('[data-testid="profile-tab-content"]');
              if (profileContent) {
                profileContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          />
        )}

        {/* Message */}
        {message.text && (
          <div className={`flex items-center gap-2 p-4 rounded-xl mb-6 ${
            message.type === 'success' ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'
          }`}>
            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {user.role === 'facilitator' && !onboardingDismissed && (
          <OnboardingChecklist
            profile={profile}
            onDismiss={dismissOnboarding}
            onGoToSection={goToProfileSection}
            onSubmit={submitForReview}
          />
        )}

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-muted/50 p-1 rounded-xl">
            <TabsTrigger value="profile" className="rounded-lg">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" data-testid="profile-tab-content">
            {user.role === 'facilitator' && (
              <div className="mb-6"><VerifiedBadgeShare profile={profile} /></div>
            )}
            {user.role === 'admin' ? (
              <div className="bg-card rounded-2xl border border-border p-6 text-center">
                <h2 className="font-heading font-semibold text-xl mb-2">Admin Account</h2>
                <p className="text-muted-foreground mb-4">
                  Admin accounts don't have public profiles. Use the Admin Panel to manage the platform.
                </p>
                <Button onClick={() => navigate('/admin')} className="rounded-full">
                  Go to Admin Panel
                </Button>
              </div>
            ) : user.role === 'facilitator' ? (
              <FacilitatorProfileForm 
                profile={profile} 
                setProfile={setProfile}
                setMessage={setMessage}
              />
            ) : (
              <CampProfileForm 
                profile={profile} 
                setProfile={setProfile}
                setMessage={setMessage}
              />
            )}
          </TabsContent>

          <TabsContent value="settings">
            <div className="space-y-6">
              {/* Account Info */}
              <div className="bg-card rounded-2xl border border-border p-6">
                <h2 className="font-heading font-semibold text-xl mb-4">Account Information</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Email</Label>
                    <div className="flex items-center gap-2">
                      <Input value={user.email} disabled className="h-12 rounded-xl bg-muted flex-1" />
                      {user.email_verified ? (
                        <span className="flex items-center gap-1 text-primary text-sm px-3 py-2 bg-primary/10 rounded-lg">
                          <MailCheck className="w-4 h-4" /> Verified
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-600 text-sm px-3 py-2 bg-amber-50 rounded-lg">
                          <Mail className="w-4 h-4" /> Unverified
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Account Type</Label>
                    <Input value={user.role} disabled className="h-12 rounded-xl bg-muted capitalize" />
                  </div>
                </div>
              </div>

              {/* Change Password */}
              <ChangePasswordForm setMessage={setMessage} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const FacilitatorProfileForm = ({ profile, setProfile, setMessage }) => {
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    bio: profile?.bio || '',
    profile_photo: profile?.profile_photo || '',
    camp_roles: profile?.camp_roles || [],
    camp_types: profile?.camp_types || [],
    age_groups: profile?.age_groups || [],
    provinces: profile?.provinces || [],
    years_experience: profile?.years_experience || 0,
    languages: profile?.languages || [],
    contact_email: profile?.contact_email || '',
    contact_phone: profile?.contact_phone || '',
    // Qualifications & Safety
    first_aid_certified: profile?.first_aid_certified || false,
    first_aid_level: profile?.first_aid_level || '',
    swimming_ability: profile?.swimming_ability || '',  // 'can_swim', 'cannot_swim', or ''
    specialties: profile?.specialties || [],
    available_for_bookings: profile?.available_for_bookings,  // true, false, or null/undefined
    references: [0, 1].map((i) => {
      const r = (profile?.references || [])[i] || {};
      return {
        id: r.id, full_name: r.full_name || '', role: r.role || '', organisation: r.organisation || '',
        contact_number: r.contact_number || '', email: r.email || '', relationship: r.relationship || '',
      };
    }),
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleReferenceChange = (idx, field, value) => {
    setFormData((prev) => {
      const refs = prev.references.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
      return { ...prev, references: refs };
    });
  };

  const toggleArrayItem = (field, item) => {
    const arr = formData[field];
    if (arr.includes(item)) {
      handleChange(field, arr.filter(i => i !== item));
    } else {
      handleChange(field, [...arr, item]);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setMessage({ type: 'error', text: 'Please upload a JPEG, PNG, or WebP image' });
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.profilePhoto(file);
      handleChange('profile_photo', response.data.url);
      setMessage({ type: 'success', text: 'Photo uploaded successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to upload photo' });
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const filledRefs = (formData.references || []).filter((r) =>
        r.full_name || r.role || r.organisation || r.contact_number || r.email || r.relationship
      );
      // Validate: every started reference must have all required fields (email optional)
      const required = ['full_name', 'role', 'organisation', 'contact_number', 'relationship'];
      const incomplete = filledRefs.some((r) => required.some((f) => !String(r[f] || '').trim()));
      if (incomplete) {
        setSaving(false);
        setMessage({ type: 'error', text: 'Please complete all required reference fields (name, role, organisation, contact number and relationship) for each reference you add.' });
        return;
      }
      const payload = { ...formData, references: filledRefs };
      if (profile) {
        await facilitatorAPI.update(profile.id, payload);
      } else {
        await facilitatorAPI.create(payload);
      }
      // Re-fetch to get the owner view (includes references)
      const fresh = await facilitatorAPI.getMyProfile();
      setProfile(fresh.data);
      setMessage({ type: 'success', text: 'Profile saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save profile' });
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-card rounded-2xl border border-border p-6">
        <h2 className="font-heading font-semibold text-xl mb-6">Basic Information</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Full Name *</Label>
            <Input
              value={formData.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              className="h-12 rounded-xl"
              required
              data-testid="profile-name"
            />
          </div>

          <div className="space-y-2">
            <Label>Profile Photo</Label>
            <div className="flex gap-2">
              <Input
                value={formData.profile_photo}
                onChange={(e) => handleChange('profile_photo', e.target.value)}
                placeholder="Upload or paste URL"
                className="h-12 rounded-xl flex-1"
                data-testid="profile-photo"
              />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                className="h-12 px-4"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload className="w-4 h-4" />
              </Button>
            </div>
            {formData.profile_photo && (
              <img 
                src={formData.profile_photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${formData.profile_photo}` : formData.profile_photo} 
                alt="Preview" 
                className="w-20 h-20 rounded-xl object-cover mt-2"
              />
            )}
          </div>

          <div className="md:col-span-2 space-y-2">
            <Label>Bio (max 300 characters) *</Label>
            <Textarea
              value={formData.bio}
              onChange={(e) => handleChange('bio', e.target.value.slice(0, 300))}
              placeholder="Tell us about yourself and your experience..."
              className="min-h-24 rounded-xl"
              required
              data-testid="profile-bio"
            />
            <p className="text-xs text-muted-foreground">{formData.bio.length}/300</p>
          </div>

          <div className="space-y-2">
            <Label>Years of Experience</Label>
            <Input
              type="number"
              min="0"
              value={formData.years_experience}
              onChange={(e) => handleChange('years_experience', parseInt(e.target.value) || 0)}
              className="h-12 rounded-xl"
              data-testid="profile-experience"
            />
          </div>

          <div className="space-y-2">
            <Label>Languages (comma separated)</Label>
            <Input
              value={formData.languages.join(', ')}
              onChange={(e) => handleChange('languages', e.target.value.split(',').map(l => l.trim()).filter(Boolean))}
              placeholder="English, Afrikaans, Zulu"
              className="h-12 rounded-xl"
              data-testid="profile-languages"
            />
          </div>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-6">
        <h2 className="font-heading font-semibold text-xl mb-6">Experience & Availability</h2>
        
        <div className="space-y-6">
          <div>
            <Label className="mb-3 block">Camp Roles</Label>
            <div className="flex flex-wrap gap-2">
              {campRoles.map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => toggleArrayItem('camp_roles', role)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    formData.camp_roles.includes(role)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-3 block">Camp Types</Label>
            <div className="flex flex-wrap gap-2">
              {campTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => toggleArrayItem('camp_types', type.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    formData.camp_types.includes(type.value)
                      ? 'bg-secondary text-secondary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-3 block">Age Groups</Label>
            <div className="flex flex-wrap gap-2">
              {ageGroups.map((age) => (
                <button
                  key={age}
                  type="button"
                  onClick={() => toggleArrayItem('age_groups', age)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    formData.age_groups.includes(age)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {age}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-3 block">Available Provinces</Label>
            <div className="flex flex-wrap gap-2">
              {provinces.map((prov) => (
                <button
                  key={prov}
                  type="button"
                  onClick={() => toggleArrayItem('provinces', prov)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    formData.provinces.includes(prov)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {prov}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Qualifications & Safety Section */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center gap-2 mb-6">
          <Shield className="w-5 h-5 text-primary" />
          <h2 className="font-heading font-semibold text-xl">Qualifications & Safety</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          This information helps parents and schools make informed decisions. Status indicators will be shown on your public profile.
        </p>
        
        <div className="space-y-6">
          {/* First Aid */}
          <div className="flex items-start gap-4">
            <Checkbox
              id="first_aid_certified"
              checked={formData.first_aid_certified}
              onCheckedChange={(checked) => handleChange('first_aid_certified', checked)}
              data-testid="first-aid-checkbox"
            />
            <div className="flex-1">
              <Label htmlFor="first_aid_certified" className="text-base cursor-pointer">
                First Aid Certified
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                I hold a valid first aid certification
              </p>
              {formData.first_aid_certified && (
                <div className="mt-3">
                  <Label className="text-sm">Certification Level</Label>
                  <Select 
                    value={formData.first_aid_level || "none"} 
                    onValueChange={(v) => handleChange('first_aid_level', v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="w-full max-w-xs h-10 rounded-xl mt-1">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select level</SelectItem>
                      {firstAidLevels.map((level) => (
                        <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Swimming Ability */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Droplets className="w-4 h-4 text-blue-500" />
              Swimming Ability
            </Label>
            <Select 
              value={formData.swimming_ability || "not_indicated"} 
              onValueChange={(v) => handleChange('swimming_ability', v === "not_indicated" ? "" : v)}
            >
              <SelectTrigger className="w-full max-w-xs h-10 rounded-xl">
                <SelectValue placeholder="Select swimming ability" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_indicated">Not indicated</SelectItem>
                <SelectItem value="can_swim">Can swim</SelectItem>
                <SelectItem value="cannot_swim">Cannot swim</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Indicate your swimming ability for water-based activities
            </p>
          </div>

          {/* Admin-verified status display */}
          {profile && (
            <div className="pt-4 border-t border-border">
              <p className="text-sm font-medium mb-3">Admin-Verified Status</p>
              <div className="flex flex-wrap gap-3">
                <div className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 ${
                  profile.id_verified 
                    ? 'bg-primary/10 text-primary' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {profile.id_verified ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
                  ID {profile.id_verified ? 'Verified' : 'Not Verified'}
                </div>
                <div className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 ${
                  profile.police_clearance === 'verified' 
                    ? 'bg-primary/10 text-primary' 
                    : profile.police_clearance === 'pending'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {profile.police_clearance === 'verified' && <CheckCircle2 className="w-3.5 h-3.5" />}
                  Police Clearance: {profile.police_clearance === 'verified' ? 'Verified' : profile.police_clearance === 'pending' ? 'Pending' : 'Not Provided'}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                These fields are verified by Campia administrators based on your uploaded documents.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Verification Documents Section */}
      <VerificationDocuments profile={profile} />

      {/* Specialties Section */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <h2 className="font-heading font-semibold text-xl mb-6">Specialties</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Select your areas of expertise. These will be displayed on your public profile.
        </p>
        
        <div className="flex flex-wrap gap-2">
          {facilitatorSpecialties.map((specialty) => (
            <button
              key={specialty}
              type="button"
              onClick={() => toggleArrayItem('specialties', specialty)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                formData.specialties.includes(specialty)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80 text-foreground'
              }`}
              data-testid={`specialty-${specialty.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {specialty}
            </button>
          ))}
        </div>
      </div>

      {/* Professional References Section */}
      <div className="bg-card rounded-2xl border border-border p-6" data-testid="references-section">
        <h2 className="font-heading font-semibold text-xl mb-2">Professional References</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Please provide two people who can speak to your experience as a camp facilitator.
          References are not displayed publicly — they are used as part of the Campia verification process.
        </p>
        <div className="space-y-6">
          {[0, 1].map((idx) => {
            const ref = formData.references[idx] || {};
            return (
              <div key={idx} className="border border-border rounded-xl p-4" data-testid={`reference-slot-${idx}`}>
                <p className="font-medium text-sm mb-3">Reference {idx + 1}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Full name *</label>
                    <Input value={ref.full_name} onChange={(e) => handleReferenceChange(idx, 'full_name', e.target.value)}
                      placeholder="Jane Smith" data-testid={`reference-${idx}-full_name`} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Role / position *</label>
                    <Input value={ref.role} onChange={(e) => handleReferenceChange(idx, 'role', e.target.value)}
                      placeholder="Camp Director" data-testid={`reference-${idx}-role`} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Organisation / camp name *</label>
                    <Input value={ref.organisation} onChange={(e) => handleReferenceChange(idx, 'organisation', e.target.value)}
                      placeholder="Sunrise Adventure Camp" data-testid={`reference-${idx}-organisation`} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Contact number *</label>
                    <Input value={ref.contact_number} onChange={(e) => handleReferenceChange(idx, 'contact_number', e.target.value)}
                      placeholder="+27 82 123 4567" data-testid={`reference-${idx}-contact_number`} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Email address (optional)</label>
                    <Input type="email" value={ref.email} onChange={(e) => handleReferenceChange(idx, 'email', e.target.value)}
                      placeholder="jane@example.com" data-testid={`reference-${idx}-email`} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-muted-foreground">Relationship / context *</label>
                    <Input value={ref.relationship} onChange={(e) => handleReferenceChange(idx, 'relationship', e.target.value)}
                      placeholder="Camp Director I reported to at Sunrise Camp" data-testid={`reference-${idx}-relationship`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Availability Section */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <h2 className="font-heading font-semibold text-xl mb-4">Availability</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Let schools and camp organisers know if you're currently available for bookings.
        </p>
        
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => handleChange('available_for_bookings', true)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              formData.available_for_bookings === true
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80 text-foreground'
            }`}
            data-testid="available-yes"
          >
            Available
          </button>
          <button
            type="button"
            onClick={() => handleChange('available_for_bookings', false)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              formData.available_for_bookings === false
                ? 'bg-muted-foreground text-muted'
                : 'bg-muted hover:bg-muted/80 text-foreground'
            }`}
            data-testid="available-no"
          >
            Not available
          </button>
          <button
            type="button"
            onClick={() => handleChange('available_for_bookings', null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              formData.available_for_bookings === null || formData.available_for_bookings === undefined
                ? 'bg-muted text-muted-foreground ring-2 ring-border'
                : 'bg-muted hover:bg-muted/80 text-foreground'
            }`}
            data-testid="available-not-indicated"
          >
            Not indicated
          </button>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-6">
        <h2 className="font-heading font-semibold text-xl mb-6">Contact Information</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Contact Email</Label>
            <Input
              type="email"
              value={formData.contact_email}
              onChange={(e) => handleChange('contact_email', e.target.value)}
              className="h-12 rounded-xl"
              data-testid="profile-contact-email"
            />
          </div>

          <div className="space-y-2">
            <Label>Contact Phone</Label>
            <Input
              value={formData.contact_phone}
              onChange={(e) => handleChange('contact_phone', e.target.value)}
              placeholder="+27 82 123 4567"
              className="h-12 rounded-xl"
              data-testid="profile-contact-phone"
            />
          </div>
        </div>
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 rounded-full"
        disabled={saving}
        data-testid="save-profile-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Profile'}
      </Button>
    </form>
  );
};

const CampProfileForm = ({ profile, setProfile, setMessage }) => {
  const [formData, setFormData] = useState({
    camp_name: profile?.camp_name || '',
    province: profile?.province || '',
    nearest_town: profile?.nearest_town || '',
    camp_type: profile?.camp_type || '',
    age_range_min: profile?.age_range_min || 6,
    age_range_max: profile?.age_range_max || 18,
    capacity: profile?.capacity || '',
    seasonality: profile?.seasonality || 'all_year',
    accreditation: profile?.accreditation || '',
    activities: profile?.activities || [],
    description: profile?.description || '',
    contact_email: profile?.contact_email || '',
    contact_phone: profile?.contact_phone || '',
    website: profile?.website || '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const toggleActivity = (activity) => {
    const arr = formData.activities;
    if (arr.includes(activity)) {
      handleChange('activities', arr.filter(i => i !== activity));
    } else {
      handleChange('activities', [...arr, activity]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    const submitData = {
      ...formData,
      capacity: formData.capacity ? parseInt(formData.capacity) : null,
    };

    try {
      if (profile) {
        const response = await campAPI.update(profile.id, submitData);
        setProfile(response.data);
      } else {
        const response = await campAPI.create(submitData);
        setProfile(response.data);
      }
      setMessage({ type: 'success', text: 'Profile saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save profile' });
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-card rounded-2xl border border-border p-6">
        <h2 className="font-heading font-semibold text-xl mb-6">Basic Information</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Camp Name *</Label>
            <Input
              value={formData.camp_name}
              onChange={(e) => handleChange('camp_name', e.target.value)}
              className="h-12 rounded-xl"
              required
              data-testid="camp-name"
            />
          </div>

          <div className="space-y-2">
            <Label>Camp Type *</Label>
            <Select value={formData.camp_type} onValueChange={(v) => handleChange('camp_type', v)}>
              <SelectTrigger className="h-12 rounded-xl" data-testid="camp-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {campTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Province *</Label>
            <Select value={formData.province} onValueChange={(v) => handleChange('province', v)}>
              <SelectTrigger className="h-12 rounded-xl" data-testid="camp-province">
                <SelectValue placeholder="Select province" />
              </SelectTrigger>
              <SelectContent>
                {provinces.map((prov) => (
                  <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Nearest Town *</Label>
            <Input
              value={formData.nearest_town}
              onChange={(e) => handleChange('nearest_town', e.target.value)}
              className="h-12 rounded-xl"
              required
              data-testid="camp-town"
            />
          </div>

          <div className="md:col-span-2 space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe your camp..."
              className="min-h-24 rounded-xl"
              data-testid="camp-description"
            />
          </div>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-6">
        <h2 className="font-heading font-semibold text-xl mb-6">Camp Details</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Minimum Age</Label>
            <Input
              type="number"
              min="0"
              value={formData.age_range_min}
              onChange={(e) => handleChange('age_range_min', parseInt(e.target.value) || 6)}
              className="h-12 rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label>Maximum Age</Label>
            <Input
              type="number"
              min="0"
              value={formData.age_range_max}
              onChange={(e) => handleChange('age_range_max', parseInt(e.target.value) || 18)}
              className="h-12 rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label>Capacity (approx.)</Label>
            <Input
              type="number"
              min="0"
              value={formData.capacity}
              onChange={(e) => handleChange('capacity', e.target.value)}
              className="h-12 rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label>Seasonality</Label>
            <Select value={formData.seasonality} onValueChange={(v) => handleChange('seasonality', v)}>
              <SelectTrigger className="h-12 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_year">All Year</SelectItem>
                <SelectItem value="school_holidays">School Holidays</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Accreditation (if any)</Label>
            <Input
              value={formData.accreditation}
              onChange={(e) => handleChange('accreditation', e.target.value)}
              className="h-12 rounded-xl"
            />
          </div>
        </div>

        <div className="mt-6">
          <Label className="mb-3 block">Activities Offered</Label>
          <div className="flex flex-wrap gap-2">
            {activities.map((activity) => (
              <button
                key={activity}
                type="button"
                onClick={() => toggleActivity(activity)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  formData.activities.includes(activity)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {activity}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-6">
        <h2 className="font-heading font-semibold text-xl mb-6">Contact Information</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Contact Email</Label>
            <Input
              type="email"
              value={formData.contact_email}
              onChange={(e) => handleChange('contact_email', e.target.value)}
              className="h-12 rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label>Contact Phone</Label>
            <Input
              value={formData.contact_phone}
              onChange={(e) => handleChange('contact_phone', e.target.value)}
              placeholder="+27 15 123 4567"
              className="h-12 rounded-xl"
            />
          </div>

          <div className="md:col-span-2 space-y-2">
            <Label>Website</Label>
            <Input
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://example.com"
              className="h-12 rounded-xl"
            />
          </div>
        </div>
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 rounded-full"
        disabled={saving}
        data-testid="save-profile-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Profile'}
      </Button>
    </form>
  );
};

const ChangePasswordForm = ({ setMessage }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setLocalError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setMessage({ type: '', text: '' });

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setLocalError('New passwords do not match');
      return;
    }

    // Validate password length
    if (formData.newPassword.length < 8) {
      setLocalError('New password must be at least 8 characters');
      return;
    }

    // Validate not same as current
    if (formData.currentPassword === formData.newPassword) {
      setLocalError('New password must be different from current password');
      return;
    }

    setSaving(true);

    try {
      await authAPI.changePassword(formData.currentPassword, formData.newPassword);
      setMessage({ type: 'success', text: 'Password changed! Please log in again with your new password.' });
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      // Password change invalidates the current session server-side — force re-login
      setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 1500);
    } catch (err) {
      setLocalError(err.response?.data?.detail || 'Failed to change password');
    }
    setSaving(false);
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-6">
      <h2 className="font-heading font-semibold text-xl mb-4">Change Password</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {localError && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl text-sm">
            <AlertCircle className="w-4 h-4" />
            {localError}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="currentPassword">Current Password</Label>
          <Input
            id="currentPassword"
            name="currentPassword"
            type="password"
            value={formData.currentPassword}
            onChange={handleChange}
            placeholder="Enter current password"
            className="h-12 rounded-xl"
            required
            data-testid="current-password"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="newPassword">New Password</Label>
          <Input
            id="newPassword"
            name="newPassword"
            type="password"
            value={formData.newPassword}
            onChange={handleChange}
            placeholder="Enter new password (min 8 characters)"
            className="h-12 rounded-xl"
            required
            data-testid="new-password"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm New Password</Label>
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm new password"
            className="h-12 rounded-xl"
            required
            data-testid="confirm-password"
          />
        </div>

        <Button 
          type="submit" 
          className="rounded-full"
          disabled={saving}
          data-testid="change-password-btn"
        >
          {saving ? 'Changing...' : 'Change Password'}
        </Button>
      </form>
    </div>
  );
};

export default Dashboard;
